/**
 * UI Components Internationalization - English (US)
 * 
 * This file contains all UI component strings for internationalization.
 * Organized by component for easy maintenance.
 */

export const UI_STRINGS = {
  // Common Actions
  common: {
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    confirm: 'Confirm',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    remove: 'Remove',
    clear: 'Clear',
    clearAll: 'Clear All',
    apply: 'Apply',
    reset: 'Reset',
    back: 'Back',
    backToDashboard: 'Back to Dashboard',
    next: 'Next',
    previous: 'Previous',
    submit: 'Submit',
    retry: 'Retry',
    refresh: 'Refresh',
    yes: 'Yes',
    no: 'No',
    ok: 'Ok',
    loading: 'Loading...',
    searching: 'Searching...',
    updating: 'Updating...',
    selectAll: 'Select All',
    all: 'All',
    none: 'None',
    show: 'Show',
    hide: 'Hide',
    creator: 'Creator',
    expandList: 'Expand list',
    collapseList: 'Collapse list',
    unknownError: 'Unknown error',
    filter: 'Filter',
    sort: 'Sort',
    search: 'Search',
    min: 'Min',
    max: 'Max',
    from: 'From',
    upTo: 'Up to',
    selected: 'selected',
    of: 'of',
    to: 'to',
    teams: 'Verticals',
    projects: 'Projects',
    features: 'Features',
    testCases: 'Test Cases',
    testRuns: 'Test Runs',
    bugs: 'Bugs',
    includeDeleted: 'Include Deleted Items',
    excludeDeleted: 'Excluded deleted items by default',
    deleted: 'Deleted',
    unknown: 'Unknown',
    unknownTeam: 'Unknown Vertical',
    unknownProject: 'Unknown Project',
    unassigned: 'Unassigned',
    notEstimated: 'Not estimated',
    viewActive: 'View Active',
    restore: 'Restore',
    softDelete: 'Soft delete',
    team: 'vertical',
    project: 'project',
    feature: 'feature',
    testCase: 'test case',
    testRun: 'test run',
    bug: 'bug',
    folder: 'folder',
    folders: 'Folders',
    maxChars: 'Maximum {max} characters',
    
    // Confirmation messages
    confirmDelete: 'Are you sure you want to delete this item?',
    confirmRestore: 'Are you sure you want to restore this item?',
    
    // Success messages
    deleteSuccess: 'Item deleted successfully!',
    restoreSuccess: 'Item restored successfully!',
    updateSuccess: 'Item updated successfully!',
    createSuccess: 'Item created successfully!',
    
    // Error messages
    deleteError: 'Failed to delete item',
    restoreError: 'Failed to restore item',
    updateError: 'Failed to update item',
    createError: 'Failed to create item',
    loadError: 'Failed to load data',
    
    // Visibility options
    visibility: {
      public: 'Public',
      private: 'Private'
    }
  },

  // Loading State
  loadingState: {
    loading: 'Loading...',
    retry: 'Retry',
    progressFormat: '{progress}%'
  },

  // Search Bar
  searchBar: {
    placeholder: 'Search...',
    searching: 'Searching...',
    filterLabel: 'Filter results',
    dropdownTitle: 'Search Results',
    noResultsFound: 'No results found for',
    resultCount: '{count} result',
    resultCountPlural: '{count} results',
    inFilter: 'in {filter}'
  },

  // Confirmation Dialog
  confirmationDialog: {
    title: 'Confirm Action',
    confirm: 'Confirm',
    cancel: 'Cancel',
    detailsLabel: 'Details:'
  },

  // Form Modal
  formModal: {
    save: 'Save',
    cancel: 'Cancel',
    reset: 'Reset'
  },

  // Page Header
  pageHeader: {
    backToDashboard: 'Back to Dashboard',
    welcomeBack: 'Welcome back, {name}!'
  },

  // Users Page
  users: {
    // Page Header
    title: 'User Management',
    description: 'Manage users and their roles across the system',
    helperText: 'View and manage user accounts, roles, and permissions',



    // Filters
    filtersTitle: 'Users Filters',
    
    // Actions
    addUser: 'Add User',
    showFilters: 'Show Filters',
    hideFilters: 'Hide Filters',
    viewDeleted: 'Show Deleted',
    hideDeleted: 'Hide Deleted',
    refreshUsers: 'Refresh',
    
    // Filters
    searchPlaceholder: 'Search by name or email...',
    searchLabel: 'Search Users',
    roleLabel: 'Role',
    teamLabel: 'Vertical',
    projectLabel: 'Project',
    allRoles: 'All Roles',
    allTeams: 'All Verticals',
    allProjects: 'All Projects',
    clearFilters: 'Clear Filters',

    // Table Headers
    userColumn: 'User',
    roleColumn: 'Role',
    teamsColumn: 'Verticals',
    projectsColumn: 'Projects',
    lastActiveColumn: 'Last Active',
    statusColumn: 'Status',
    actionsColumn: 'Actions',

    // Table Content
    unknownUser: 'Unknown User',
    noTeams: 'No verticals',
    noProjects: 'No projects',
    moreTeams: '+{count} more',
    moreProjects: '+{count} more',
    showLess: 'Show less',
    active: 'Active',
    
    // Actions
    editRole: 'Edit Role',
    deleteUser: 'Delete User',
    restoreUser: 'Restore User',
    deleted: 'Deleted',
    
    // Empty States
    noUsersFound: 'No users found',
    noUsersDescription: 'Try adjusting your search or filter criteria.',
    loadingUsers: 'Loading users...',
    
    // Edit Modal
    addUserTitle: 'Add New User',
    editUserTitle: 'Edit User Role and Permissions',
    
    // Restore Modal
    restoreUserTitle: 'Restore User Access',
    restoreConfirmTitle: 'Restore User?',
    restoreConfirmDescription: 'You are about to restore access for {name}.',
    userDetails: 'User Details',
    restoreWarning: 'This will restore the user\'s access to all their previous verticals and projects.',
    restoreUserButton: 'Restore User',
    restoring: 'Restoring...',
    nameLabel: 'Name',
    namePlaceholder: 'Enter user name',
    emailLabel: 'Email',
    emailPlaceholder: 'Enter user email',
    passwordLabel: 'Password',
    passwordPlaceholder: 'Enter password (min. 6 characters)',
    userInformation: 'User Information',
    userRole: 'User Role',
    roleRequired: 'Role ',
    teamProjectAccess: 'Vertical & Project Access',
    teamsTitle: 'Verticals',
    teamsDescription: 'Click vertical name to view projects, click checkbox to select vertical',
    projectsTitle: 'Projects',
    projectsInTeam: 'Projects in {teamName}',
    selectTeamPrompt: 'Select a vertical to view projects',
    searchTeams: 'Search verticals...',
    searchProjects: 'Search projects...',
    loadingTeams: 'Loading verticals...',
    loadingProjects: 'Loading projects...',
    noTeamsAvailable: 'No verticals available',
    noTeamsFoundSearch: 'No verticals found',
    noProjectsInTeam: 'No projects in this vertical',
    noProjectsFoundSearch: 'No projects found',
    selectedTeams: 'Selected Verticals:',
    selectedProjects: 'Selected Projects:',
    createUser: 'Create User',
    creating: 'Creating...',
    updateUser: 'Update User',
    userLabel: 'User Profile',
    
    // Roles
    roleSuperAdmin: 'Super Admin',
    roleOwner: 'Owner',
    roleUser: 'User',
    
    // Success/Error Messages
    userCreatedSuccess: 'User created successfully',
    userUpdatedSuccess: 'User updated successfully',
    yourPermissionsUpdated: 'Your vertical and project permissions updated successfully',
    userDeletedSuccess: 'User deleted successfully',
    userRestoredSuccess: 'User restored successfully',
    failedToCreateUser: 'Failed to create user',
    failedToLoadUsers: 'Failed to load users',
    failedToLoadTeams: 'Failed to load verticals',
    failedToLoadProjects: 'Failed to load projects',
    failedToUpdateUser: 'Failed to update user',
    failedToDeleteUser: 'Failed to delete user',
    failedToRestoreUser: 'Failed to restore user',
    emailAlreadyExists: 'A user with this email already exists',
    pleaseFillAllFields: 'Please fill in all required fields',
    passwordTooShort: 'Password must be at least 6 characters long',
    onlySuperAdminCanUpdate: 'Only Super Admin can update user roles',
    onlySuperAdminCanUpdateAdminRoles: 'Only Super Admin can modify Admin roles',
    cannotChangeOwnRole: 'You cannot change your own role',
    insufficientPermissions: 'You do not have permission to perform this action',
    
    // Confirmation
    deleteConfirmTitle: 'Delete User',
    deleteConfirmDescription: 'Are you sure you want to remove access for {name}?',
    deleteWarning: 'This will deactivate the user account. You can restore access anytime from the "View Deleted" section.',
    deleteFinalConfirmation: 'Final Confirmation',
    deleting: 'Deleting...',
    associations: 'Vertical & Project Associations',
    
    // Formatting
    neverActive: 'Never'
  },

  // Enhanced Table
  enhancedTable: {
    loading: 'Loading...',
    searchPlaceholder: 'Search...',
    noData: 'No data',
    noDataDescription: 'No data available to display.',
    showLabel: 'Show:',
    selectedCount: '{count} of {total} selected',
    previous: 'Previous',
    next: 'Next',
    showing: 'Showing',
    to: 'to',
    of: 'of',
    results: 'results'
  },

  // Table
  table: {
    noData: 'No data',
    noDataDescription: 'No data available to display.',
    previous: 'Previous',
    next: 'Next',
    showing: 'Showing',
    to: 'to',
    of: 'of',
    results: 'results'
  },

  // Multi Select
  multiSelect: {
    placeholder: 'Select options...',
    selectAll: 'Select All',
    selected: '{count} selected',
    noOptions: 'No options found'
  },

  // Top Navigation
  topNav: {
    dashboard: 'Dashboard',
    aiAssistant: 'Integrations',
    notifications: 'Notifications',
    markAllAsRead: 'Mark all as read',
    noNotifications: 'No notifications',
    viewAllNotifications: 'View all notifications',
    profileSettings: 'Profile Settings',
    teamManagement: 'Vertical Management',
    userManagement: 'User Management',
    signOut: 'Sign out',
    openMainMenu: 'Open main menu',
    switchToLightMode: 'Switch to light mode',
    switchToDarkMode: 'Switch to dark mode',
    notificationsLabel: 'Notifications',
    openUserMenu: 'Open user menu',
    chromeExtension: 'Chrome Extension',
    aiTestCaseGenerator: 'AI Test Case Generator',
    whereYouAreMentioned: "Notifications",
    whereYouAreMentionedDescription: 'Items where you are assigned or tagged',
    viewAssignedToMe: 'View all',
    assignedFeatures: 'Features',
    assignedBugs: 'Bugs',
    assignedTestRuns: 'Test Runs',
    assignedAccessibility: 'Accessibility Issues',
    youHaveBeenTaggedUnder: 'You have been tagged under {id}',
    youHaveCreatedTask: 'You have created this task: {id}',
    noAssignedItems: 'No items assigned to you.',
    markAsRead: 'Mark as read'
  },

  // Modal
  modal: {
    close: 'Close',
    closeModal: 'Close modal'
  },

  // Empty State
  emptyState: {
    noData: 'No data',
    noDataDescription: 'No data available to display.'
  },

  // Button (for accessibility)
  button: {
    loading: 'Loading...',
    iconOnly: 'Button action'
  },

  // Input
  input: {
    showPassword: 'Show password',
    hidePassword: 'Hide password'
  },

  // Link
  link: {
    externalLink: 'Opens in new window'
  },

  // Dropdown
  dropdown: {
    selectOption: 'Select option'
  },

  // Icon
  icon: {
    decorative: 'Decorative icon'
  },

  // Tooltip
  tooltip: {
    showTooltip: 'Show tooltip'
  },

  // Time formats
  time: {
    minutesAgo: '{minutes}m ago',
    hoursAgo: '{hours}h ago',
    daysAgo: '{days}d ago',
    justNow: 'Just now'
  },

  // Status labels
  status: {
    active: 'Active',
    inactive: 'Inactive',
    pending: 'Pending',
    completed: 'Completed',
    cancelled: 'Cancelled',
    failed: 'Failed',
    success: 'Success',
    error: 'Error',
    warning: 'Warning',
    info: 'Info'
  },

  // Accessibility
  aria: {
    loading: 'Loading content',
    search: 'Search',
    close: 'Close',
    menu: 'Menu',
    previous: 'Previous page',
    next: 'Next page',
    sortAscending: 'Sort ascending',
    sortDescending: 'Sort descending',
    select: 'Select',
    selectAll: 'Select all',
    deselectAll: 'Deselect all',
    dashboardStatistics: 'Dashboard statistics',
    formFields: 'Form fields',
    formActions: 'Form actions'
  },

  // Dashboard Components
  dashboard: {
    title: 'Dashboard Overview',
    helperTextMobile: 'Tap a vertical to view projects',
    helperTextDesktop: 'Click on any vertical name below to view its projects list and details',
    searchPlaceholder: 'Search...',
    searchPlaceholderFull: 'Search verticals, projects, features, test cases, test runs, bugs...',
    filterSearchResults: 'Filter search results',
    searchResultsTitle: 'Search Results',
    noSearchResultsFor: 'No results found for',
    viewReports: 'View Reports',
    viewReportsShort: 'Reports',
    chromeExtension: 'Chrome Extension',
    chromeExtensionShort: 'Extension',
    refresh: 'Refresh',
    errorLoadingData: 'Failed to load dashboard data.',
    loadingOverviewMetrics: 'Loading metrics…',
    activeTeams: 'Active verticals',
    totalProjects: 'Total projects',
    openParentFeatures: 'Open features',
    activeTestCases: 'Active test cases',
    openBugs: 'Open bugs',
    unassignedProjects: 'Unassigned Projects',
    unnamedTeam: 'Unnamed Vertical',
    unknown: 'Unknown',
    untitledProject: 'Untitled Project',
    noDescription: 'No description',
    teamWithProjects: 'Vertical with {count} project',
    teamWithProjectsPlural: 'Vertical with {count} projects',
    teamLabel: 'Vertical',
    projectLabel: 'Project:',
    teamsProjects: 'Verticals & Projects',
    manageTeamsProjects: 'Manage Verticals & Projects',
    noTeamsFound: 'No verticals found',
    noTeamsDescription: 'Get started by creating your first vertical',
    noTeamsDescriptionUser: 'Please contact your administrator to create verticals and projects.',
    createTeam: 'New Vertical',
    selectTeam: 'Select a vertical to view its projects',
    noProjectsInTeam: 'No projects in this vertical',
    noProjectsDescription: 'Get started by creating your first project',
    noProjectsDescriptionUser: 'Please contact your administrator to create a new project.',
    createProject: 'New Project',
    features: '{count} feature',
    featuresPlural: '{count} features',
    testCases: '{count} test case',
    testCasesPlural: '{count} test cases',
    testRuns: '{count} test run',
    testRunsPlural: '{count} test runs',
    bugs: '{count} bug',
    bugsPlural: '{count} bugs',
    projects: '{count} project',
    projectsPlural: '{count} projects',
    statusLabel: 'Status:',
    unknownType: 'Unknown'
  },

  integrations: {
    title: 'Integrations',
  },

  chromeExtension: {
    title: 'Chrome Extension',
    description: 'Download and install the Assertly extension for Chrome (local use, not in store).',
    installTitle: 'Add to Chrome',
    installSubtitle: 'Load the extension in Chrome (developer mode).',
    step1: 'Download the zip using the button above and unzip it to a folder on your computer.',
    step2: 'Open Chrome and go to chrome://extensions',
    step3: 'Turn on "Developer mode" (toggle in the top-right).',
    step4: 'Click "Load unpacked" and select the unzipped folder (assertly-chrome-extension).',
    step5: 'The Assertly extension will appear in your extensions and in the Chrome toolbar.',
    note: 'This extension is not published in the Chrome Web Store. It is intended for use only within your organization.',
    downloadTitle: 'Download extension',
    downloadDescription: 'This extension is for local use. Download the zip and load it in Chrome as an unpacked extension.',
    versionsLabel: 'Latest versions from GitHub (max 4). If fewer exist, only those are shown:',
    syncReleases: 'Sync',
    loadingReleases: 'Loading releases…',
    releasesError: 'Could not load releases from GitHub.',
    releasesErrorHint: 'Set VITE_GITHUB_EXTENSION_REPO (owner/repo) in .env and ensure the repo has releases.',
    noReleases: 'No releases found.',
    tableVersion: 'Version',
    tableDownload: 'Download',
    downloadZip: 'Download zip',
    noZipAsset: 'No zip asset'
  },

  // Error Pages
  errorPages: {
    notFound: {
      code: '404',
      title: 'This Page Failed the Test',
      message: "This URL didn't pass our QA checks. Maybe it's out fixing bugs!",
      suggestion: 'Try these tested pages:',
      goToDashboard: 'Take Me Home',
      goBack: 'Go Back',
      ariaLabel: {
        icon: 'Error page icon',
        errorCode: 'Error code 404',
        quickLinks: 'Quick navigation links',
        primaryActions: 'Primary navigation actions'
      },
      links: {
        dashboard: 'Dashboard',
        testRuns: 'Test Runs',
        bugs: 'Bugs',
        reports: 'Reports',
        profile: 'Profile'
      }
    }
  },

  // Profile Page
  profile: {
    title: 'Profile Settings',
    description: 'Manage your account settings and preferences',
    theme: {
      light: 'Light',
      dark: 'Dark'
    },
    changePassword: 'Change Password',
    fields: {
      fullName: 'Full Name',
      email: 'Email Address',
      role: 'Role',
      bio: 'Bio',
      bioPlaceholder: 'Tell us about yourself...',
      currentPassword: 'Current Password',
      newPassword: 'New Password',
      confirmPassword: 'Confirm New Password'
    },
    placeholders: {
      currentPassword: 'Enter current password',
      newPassword: 'Enter new password (min 6 characters)',
      confirmPassword: 'Confirm new password'
    },
    buttons: {
      saveChanges: 'Save Changes',
      saving: 'Saving...',
      cancel: 'Cancel',
      updatePassword: 'Update Password',
      updating: 'Updating...'
    },
    modal: {
      changePasswordTitle: 'Change Password'
    },
    errors: {
      nameRequired: 'Name is required',
      currentPasswordRequired: 'Current password is required',
      newPasswordRequired: 'New password is required',
      passwordMinLength: 'Password must be at least 6 characters',
      passwordsDoNotMatch: 'Passwords do not match',
      currentPasswordIncorrect: 'Current password is incorrect',
      passwordSameAsCurrent: 'New password must be different from current password'
    },
    success: {
      profileUpdated: 'Profile updated successfully!',
      passwordChanged: 'Password changed successfully!'
    },
    failure: {
      profileUpdate: 'Failed to update profile',
      passwordChange: 'Failed to change password'
    }
  },

  // Reports & Analytics
  reports: {
    title: 'Analytics & Reports',
    description: 'Comprehensive insights from your test management data',
    helperText: 'View trends, track metrics, and export data',
    exportAll: 'Export All Data',
    exportReport: 'Export Report',
    exportSection: 'Export {section}',
    generatedOn: 'Generated on: {date}',
    totalRecords: 'Total Records: {count}',
    recordsCount: '{count} records',
    showingFirst: 'Showing first {count} of {total} results. Export to see all.',
    noDataToExport: 'No data to export',
    exportSuccess: 'Report exported successfully ({count} records)',
    exportAllSuccess: 'Complete analytics report exported successfully ({count} total records across {sheets} sheets)',
    exportFailed: 'Failed to export data. Please try again.',
    loading: 'Loading analytics data...',
    loadingFailed: 'Failed to load analytics data',
    noDataAvailable: 'No data available',
    filtersReset: 'Filters reset',
    exportedRecords: 'Exported {count} records',
    noDataForSection: 'No {section} data to export',
    invalidSection: 'Invalid section for export',
    
    tabs: {
      ariaLabel: 'Report tabs',
      overview: 'Overview',
      teams: 'Verticals & Projects',
      features: 'Features',
      testCases: 'Test Cases',
      testRuns: 'Test Runs',
      bugs: 'Bugs',
      accessibility: 'Accessibility'
    },

    sections: {
      teamsAndProjects: 'Verticals & Projects',
      teams: 'Verticals',
      projects: 'Projects',
      features: 'Features',
      testCases: 'Test Cases',
      testRuns: 'Test Runs',
      bugs: 'Bugs'
    },
    
    filter: {
      title: 'Filters',
      show: 'Show Filters',
      hide: 'Hide Filters',
      clearAll: 'Clear All',
      apply: 'Apply Filters',
      reset: 'Reset Filters',
      search: 'Search {section}...',
      searchPlaceholder: 'Search...',
      showingCount: 'Showing {filtered} of {total} {section}',
      showingCountWithDeleted: 'Showing {filtered} of {total} {section} (including deleted)',
      showingTeamsAndProjects: 'Showing {filteredTeams} of {totalTeams} verticals & {filteredProjects} of {totalProjects} projects',
      showingTeamsAndProjectsWithDeleted: 'Showing {filteredTeams} of {totalTeams} verticals & {filteredProjects} of {totalProjects} projects (including deleted)',
      activeFilters: '{count} filter(s) active',
      noActiveFilters: 'No active filters',
      
      fields: {
        teams: 'Vertical',
        projects: 'Project',
        features: 'Feature',
        status: 'Status',
        priority: 'Priority',
        severity: 'Severity',
        type: 'Type',
        environment: 'Environment',
        testType: 'Test Type',
        caseType: 'Case Type',
        automationStatus: 'Automation Status',
        developer: 'Developer',
        qaEngineer: 'QA Engineer',
        assignee: 'Assignee',
        createdBy: 'Created By',
        assignedTo: 'Assigned To',
        estimationRange: 'Estimation Range (minutes)',
        startDate: 'Start Date',
        endDate: 'End Date',
        dateRange: 'Date Range'
      },
      
      placeholders: {
        allTeams: 'All verticals',
        allProjects: 'All projects',
        allFeatures: 'All features',
        allStatuses: 'All statuses',
        allPriorities: 'All priorities',
        allSeverities: 'All severities',
        allTypes: 'All types',
        allEnvironments: 'All environments',
        allDevelopers: 'All developers',
        allQAEngineers: 'All QA engineers',
        allTestTypes: 'All test types',
        allCaseTypes: 'All case types',
        allAutomationStatuses: 'All automation statuses',
        allAssignees: 'All assignees',
        allCreators: 'All creators',
        selectDate: 'Select date',
        fromDate: 'From',
        toDate: 'To'
      },
      
      options: {
        unassigned: 'Unassigned'
      },
      
      ariaLabels: {
        startDateFrom: 'Start date from',
        startDateTo: 'Start date to',
        dueDateFrom: 'Due date from',
        dueDateTo: 'Due date to',
        estimationMin: 'Minimum estimation minutes',
        estimationMax: 'Maximum estimation minutes'
      },
      
      labels: {
        selectTeams: 'Vertical',
        selectProjects: 'Project',
        filterByTeams: 'Vertical',
        developer: 'Developer',
        qaEngineer: 'QA Engineer',
        assignee: 'Assignee',
        createdBy: 'Created By',
        assignedTo: 'Assigned To',
        estimationRange: 'Estimation Range (minutes)',
        startDateRange: 'Start Date Range',
        dueDateRange: 'Due Date Range',
        testType: 'Test Type',
        caseType: 'Case Type',
        automationStatus: 'Automation Status',
        includeSubTasks: 'Include Sub-features'
      }
    },
    
    chart: {
      noData: 'No data available',
      loading: 'Loading chart...',
      byStatus: 'By Status',
      byPriority: 'By Priority',
      bySeverity: 'By Severity',
      byType: 'By Type',
      byEnvironment: 'By Environment',
      timeline: 'Timeline',
      overTime: 'Over Time',
      createdOverTime: '{entity} Created Over Time',
      distribution: 'Distribution',
      comparison: 'Comparison',
      breakdown: 'Breakdown',
      
      // Verticals & Projects
      teamsAddedOverTime: 'Verticals Added Over Time',
      teamsBreakdown: 'Verticals Breakdown',
      projectsOverview: 'Projects Overview',
      projectsBreakdown: 'Projects Breakdown',
      projectsCreatedOverTime: 'Projects Created Over Time',
      
      // Test Cases
      automatedVsManual: 'Automated vs Manual',
      testCasesByType: 'By Test Type',
      testCasesByCaseType: 'By Case Type',
      testCasesByPriority: 'By Priority',
      testCasesByStatus: 'By Status',
      testCasesByAssignee: 'By Assignee',
      testCasesByTeam: 'By Vertical',
      testCasesByProject: 'By Project',
      testCasesByEstimation: 'By Estimation Range',
      coverage: 'Automation Coverage',
      testCasesCreatedOverTime: 'Created Over Time',

      // Test Runs
      testRunByStatus: 'By Status',
      testRunByEnvironment: 'By Environment',
      testRunCompletionRate: 'Completion Rate',
      testRunByAssignee: 'By Assignee',
      testRunByCreatedBy: 'By Created By',
      testRunByEstimationRange: 'By Estimation Range',
      completionRate: 'Completion Rate',
      
      // Chart data labels
      teamsLabel: 'Verticals',
      projectsLabel: 'Projects',
      featuresLabel: 'Features',
      testCasesLabel: 'Test Cases',
      testRunsLabel: 'Test Runs',
      bugsLabel: 'Bugs',
      
      // Bug type labels
      bugTypeLabel: 'Bug',
      featureRequestLabel: 'Feature Request',
      statusOverTime: 'Status Over Time',
      testRunsCreatedOverTime: 'Created Over Time',
      
      // Bugs
      bugsByStatus: 'By Status',
      bugsBySeverity: 'By Severity',
      bugsByPriority: 'By Priority',
      bugsByType: 'By Type',
      bugsByEnvironment: 'By Environment',
      bugsByTeam: 'By Vertical',
      bugsByProject: 'By Project',
      bugsByAssignee: 'By Assignee',
      bugsCreatedOverTime: 'Created Over Time',
      criticalHighPriorityBugs: 'Critical & High Priority',

      // Features breakdown
      featuresByStatus: 'By Status',
      featuresByPriority: 'By Priority',
      featuresByTeam: 'By Vertical',
      featuresByProject: 'By Project',
      featuresByDeveloper: 'By Developer',
      featuresByQA: 'By QA'
    },
    
    table: {
      // Common column headers
      team: 'Vertical',
      projects: 'Projects',
      features: 'Features',
      testCases: 'Test Cases',
      bugs: 'Bugs',
      title: 'Title',
      status: 'Status',
      environment: 'Environment',
      progress: 'Progress',
      project: 'Project',
      created: 'Created',
      id: 'Id',
      type: 'Type',
      priority: 'Priority',
      automation: 'Automation',
      severity: 'Severity',
      name: 'Name',
      
      // Count labels
      projectsCount: 'Projects',
      featuresCount: 'Features',
      testCasesCount: 'Test Cases',
      testRunsCount: 'Test Runs',
      bugsCount: 'Bugs',
      developer: 'Developer',
      qa: 'Qa',
      assignedTo: 'Assigned To',
      createdBy: 'Created By',
      startDate: 'Start Date',
      dueDate: 'Due Date',
      
      // Other
      filtered: 'Filtered {entity} ({count})',
      export: 'Export',
      automationStatus: 'Automation Status'
    },
    
    stats: {
      // Overview
      teams: 'Verticals',
      projects: 'Projects',
      features: 'Features',
      testCases: 'Test Cases',
      testRuns: 'Test Runs',
      bugs: 'Bugs',
      
      // Subtexts
      active: 'Active',
      total: 'Total',
      parent: 'Parent',
      reported: 'Reported',
      allTeams: 'All verticals',
      allProjects: 'All projects',
      combined: 'Combined',
      all: 'All',
      pending: 'Pending',
      working: 'Working',
      reviewing: 'Reviewing',
      qa: 'Qa',
      done: 'Done',
      fullAuto: 'Full auto',
      partial: 'Partial',
      byHand: 'By hand',
      queued: 'Queued',
      running: 'Running',
      onHold: 'On hold',
      finished: 'Finished',
      stopped: 'Stopped',
      fixing: 'Fixing',
      fixed: 'Fixed',
      recurred: 'Recurred',
      stuck: 'Stuck',
      
      // Priority/Severity labels
      critical: 'Critical',
      high: 'High',
      
      // Status labels
      open: 'Open',
      inProgress: 'In Progress',
      inReview: 'In Review',
      testing: 'Testing',
      closed: 'Closed',
      resolved: 'Resolved',
      reopened: 'Reopened',
      blocked: 'Blocked',
      passed: 'Passed',
      failed: 'Failed',
      skipped: 'Skipped',
      automated: 'Automated',
      semiAutomated: 'Semi-Automated',
      manual: 'Manual',
      yetToStart: 'Yet to Start',
      paused: 'Paused',
      completed: 'Completed',
      cancelled: 'Cancelled',
      notStarted: 'Not Started'
    },
    
    overview: {
      movedToDashboard:
        'Summary metrics and the system activity timeline are now shown on the Dashboard.',
      activityTimeline: 'System Activity Timeline',
      activityTimelineSubtext: 'Weekly cumulative growth over last 12 weeks',
      qualityMetrics: 'Quality Metrics',
      qualityMetricsSubtext: 'Key health indicators',
      
      metrics: {
        testCoverage: 'Test Coverage',
        testCoverageSubtext: 'Test cases per feature',
        testCoverageDescription: 'Measures how well features are covered by test cases',
        
        automationReadiness: 'Automation Readiness',
        automationReadinessSubtext: 'Automated test percentage',
        automationReadinessDescription: 'Percentage of test cases that are automated',
        
        bugToFeatureRatio: 'Bug-to-Feature Ratio',
        bugToFeatureRatioSubtext: 'Bugs per feature',
        bugToFeatureRatioDescription: 'Average number of bugs per feature (lower is better)',
        
        testRunCompletion: 'Test Run Completion',
        testRunCompletionSubtext: 'Completed runs percentage',
        testRunCompletionDescription: 'Percentage of test runs that are completed',
        
        trend30Days: '30-day trend',
        trendUp: 'Trending up',
        trendDown: 'Trending down',
        trendStable: 'Stable',
        noTrendData: 'No trend data',
        notApplicable: 'N/A',
        noDivisionByZero: 'No data to calculate'
      },
      
      activityLabels: {
        teams: 'Verticals',
        projects: 'Projects',
        features: 'Features',
        testCases: 'Test Cases',
        testRuns: 'Test Runs',
        bugs: 'Bugs'
      },
      
      metricsInfo: {
        ariaLabel: 'Information about quality metrics calculations',
        tooltipContent: `METRIC FORMULAS:

Test Coverage: Total Test Cases ÷ Total Features
→ Average test cases per feature

Automation Readiness: (Automated Tests ÷ Total Tests) × 100
→ Percentage of automated tests

Bug-to-Feature Ratio: Total Bugs ÷ Total Features
→ Average bugs per feature (lower is better)

Test Run Completion: (Completed Runs ÷ Total Runs) × 100
→ Percentage of completed runs


TREND CALCULATION:

Trends compare current metrics against data from 30 days ago:
• Positive % (e.g., +15%) = Metric increased (trending up)
• Negative % (e.g., -10%) = Metric decreased (trending down)
• 0% = No change (stable)

Example: +15% means the metric is 15% higher than 30 days ago.`
      }
    },
    
    dateRange: {
      today: 'Today',
      yesterday: 'Yesterday',
      last7Days: 'Last 7 Days',
      last30Days: 'Last 30 Days',
      last90Days: 'Last 90 Days',
      thisWeek: 'This Week',
      thisMonth: 'This Month',
      thisYear: 'This Year',
      allTime: 'All Time',
      custom: 'Custom Range',
      from: 'From',
      to: 'To',
      selectRange: 'Select date range'
    },
    
    export: {
      // Report titles
      teamsReportTitle: 'Verticals Report',
      projectsReportTitle: 'Projects Report',
      featuresReportTitle: 'Features Report',
      testCasesReportTitle: 'Test Cases Report',
      testRunsReportTitle: 'Test Runs Report',
      bugsReportTitle: 'Bugs Report',
      
      // Sheet names
      teamsSheetName: 'Verticals',
      projectsSheetName: 'Projects',
      featuresSheetName: 'Features',
      testCasesSheetName: 'Test Cases',
      testRunsSheetName: 'Test Runs',
      bugsSheetName: 'Bugs',
      
      // Report metadata
      generatedOn: 'Generated on: {date}',
      totalTeams: 'Total Verticals: {count}',
      totalProjects: 'Total Projects: {count}',
      totalFeatures: 'Total Features: {count}',
      totalTestCases: 'Total Test Cases: {count}',
      totalTestRuns: 'Total Test Runs: {count}',
      totalBugs: 'Total Bugs: {count}',
      totalRecords: 'Total Records: {count}',
      
      // Section-specific report titles
      sectionReportTitle: '{section} REPORT - Generated on {date}',
      
      // Column headers
      headers: {
        // Verticals
        teamName: 'Vertical Name',
        totalProjects: 'Total Projects',
        totalFeatures: 'Total Features',
        totalTestCases: 'Total Test Cases',
        totalBugs: 'Total Bugs',
        
        // Projects
        projectName: 'Project Name',
        team: 'Vertical',
        features: 'Features',
        testCases: 'Test Cases',
        testRuns: 'Test Runs',
        bugs: 'Bugs',
        
        // Features
        featureId: 'Feature Id',
        title: 'Title',
        project: 'Project',
        status: 'Status',
        priority: 'Priority',
        developer: 'Developer',
        qa: 'Qa',
        startDate: 'Start Date',
        dueDate: 'Due Date',
        createdDate: 'Created Date',
        
        // Test Cases
        testCaseId: 'Test Case Id',
        type: 'Type',
        caseType: 'Case Type',
        automationStatus: 'Automation Status',
        
        // Test Runs
        testRunId: 'Test Run Id',
        environment: 'Environment',
        progress: 'Progress',
        
        // Bugs
        bugId: 'Bug Id',
        severity: 'Severity'
      },
      
      // Table headers (for data tables)
      table: {
        filtered: 'Filtered {entity}: {count}',
        automationStatus: 'Automation Status'
      },
      
      // Misc
      noStatus: 'No status',
      unknown: 'Unknown',
      filename: {
        completeAnalytics: 'complete-analytics-report-{timestamp}.xlsx',
        teamsAndProjects: 'verticals-and-projects-report-{timestamp}.xlsx',
        teams: 'verticals-report-{timestamp}.csv',
        projects: 'projects-report-{timestamp}.csv',
        features: 'features-report-{timestamp}.csv',
        testCases: 'test-cases-report-{timestamp}.csv',
        testRuns: 'test-runs-report-{timestamp}.csv',
        bugs: 'bugs-report-{timestamp}.csv'
      }
    }
  },

  // Date Picker
  datePicker: {
    selectDate: 'Select date',
    today: 'Today',
    clear: 'Clear',
    close: 'Close',
    months: {
      january: 'January',
      february: 'February',
      march: 'March',
      april: 'April',
      may: 'May',
      june: 'June',
      july: 'July',
      august: 'August',
      september: 'September',
      october: 'October',
      november: 'November',
      december: 'December'
    },
    monthsShort: {
      jan: 'Jan',
      feb: 'Feb',
      mar: 'Mar',
      apr: 'Apr',
      may: 'May',
      jun: 'Jun',
      jul: 'Jul',
      aug: 'Aug',
      sep: 'Sep',
      oct: 'Oct',
      nov: 'Nov',
      dec: 'Dec'
    },
    days: {
      sunday: 'Sunday',
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday'
    },
    daysShort: {
      sun: 'Sun',
      mon: 'Mon',
      tue: 'Tue',
      wed: 'Wed',
      thu: 'Thu',
      fri: 'Fri',
      sat: 'Sat'
    },
    daysMin: {
      su: 'Su',
      mo: 'Mo',
      tu: 'Tu',
      we: 'We',
      th: 'Th',
      fr: 'Fr',
      sa: 'Sa'
    }
  },

  // Pagination
  pagination: {
    showing: 'Showing',
    to: 'to',
    of: 'of',
    results: 'results',
    page: 'Page {page}',
    firstPage: 'First page',
    lastPage: 'Last page',
    previousPage: 'Previous page',
    nextPage: 'Next page'
  },

  // Avatar Group
  avatarGroup: {
    more: '+{count} more',
    showAll: 'Show all {count} members',
    viewAll: 'View All'
  },

  // Vertical Modal
  team: {
    createTitle: 'Create New Vertical',
    nameLabel: 'Vertical Name',
    nameRequired: 'Vertical Name *',
    namePlaceholder: 'Enter vertical name',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'Enter vertical description',
    membersLabel: 'Vertical Members',
    membersRequired: 'Vertical Members *',
    membersSearchPlaceholder: 'Search users by name or email...',
    noUsersFound: 'No users found',
    unknownUser: 'Unknown User',
    membersHelp: 'Click on users to select them. The vertical creator is automatically included.',
    visibilityLabel: 'Vertical Visibility',
    visibilityPublic: 'Public',
    visibilityPublicDescription: 'Visible to all vertical members',
    visibilityPrivate: 'Private',
    visibilityPrivateDescription: 'Only visible to Super Admins and vertical members',
    visibilityHelp: 'Private verticals are only visible to Super Admins and vertical members',
    creating: 'Creating...',
    createButton: 'New Vertical',
    createdSuccess: 'Vertical created successfully!',
    createdFailed: 'Failed to create vertical. Please try again.',
    loadingUsers: 'Loading users...',
    userNotAuthenticated: 'You must be logged in to create a vertical.'
  },

  // Project Modal
  project: {
    navigation: 'Project Navigation',
    
    // Overview Page
    overview: {
      // Stat Labels
      featuresLabel: 'Features',
      testCasesLabel: 'Test Cases',
      testRunsLabel: 'Test Runs',
      bugsLabel: 'Bugs',
      openBugsLabel: 'Open Bugs',
      teamMembersLabel: 'Vertical Members',

      // Stat Subtexts (meaningful descriptions shown under each title)
      parentFeatures: 'Scope or parent modules being tested',
      totalCases: 'Coverage across the project',
      executions: 'Test runs executed',
      totalBugs: 'Issues reported in this project',
      openBugsSubtext: 'Awaiting triage or fix',
      activeUsers: 'People active on this project',

      // Stat value labels (small caption under the big number)
      totalLabel: 'Total',
      openLabel: 'Open',
      
      // Defaults
      unknownCreator: 'Unknown',
      untitledProject: 'Untitled Project',
      noDescription: 'No description available',
      activeStatus: 'active',

      // Stability index card
      stabilityIndexBlurb:
        'Project wide stability index. It combines how your tests are doing, how much is automated, and whether bugs are getting fixed.'
    },
    
    createTitle: 'Create New Project',
    titleLabel: 'Project Title',
    titleRequired: 'Project Title ',
    titlePlaceholder: 'Enter project title',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'Enter project description',
    teamLabel: 'Vertical',
    teamRequired: 'Vertical *',
    selectTeam: 'Select a vertical',
    createNewTeam: 'Create New Vertical',
    newTeamNameLabel: 'New Vertical Name',
    newTeamNameRequired: 'New Vertical Name *',
    newTeamNamePlaceholder: 'Enter vertical name',
    statusLabel: 'Status',
    typeLabel: 'Project Type',
    membersLabel: 'Project Members',
    membersRoleFilterLabel: 'User roles',
    membersRoleFilterPlaceholder: 'All roles — filter who appears below',
    membersUsersLabel: 'Users',
    membersPlaceholder: 'Select project members',
    membersSearchPlaceholder: 'Search members...',
    membersCount: '{count} member{plural} selected',
    loadingUsers: 'Loading users...',
    noUsersAvailable: 'No users available',
    noUsersFound: 'No users found matching your search',
    visibilityLabel: 'Project Visibility',
    visibilityPublic: 'Public',
    visibilityPublicDescription: 'Visible to all vertical members',
    visibilityPrivate: 'Private',
    visibilityPrivateDescription: 'Only visible to Super Admins and project members',
    visibilityHelp: 'Private projects are only visible to Super Admins and project members',
    creating: 'Creating...',
    
    // Charts
    charts: {
      featuresByStatus: 'Features by Status',
      automationStatus: 'Automation Status',
      bugsByStatus: 'Bugs by Status',
      noFeaturesMessage: 'Create your first feature to see statistics here.',
      noTestCasesMessage: 'Create your first test case to see automation status here.',
      noBugsMessage: 'Report your first bug to see statistics here.',
      createFeatureAction: 'Create Feature',
      createTestCaseAction: 'Create Test Case',
      reportBugAction: 'Report Bug'
    },
    createButton: 'New Project',
    createdSuccess: 'Project created successfully!',
    createdFailed: 'Failed to create project. Please try again.',
    userNotAuthenticated: 'You must be logged in to create a project.',
    duplicateError: 'A project with this name already exists. Please choose a different name.',
    networkError: 'Network error. Please check your connection and try again.',
    validationError: 'Please check your input and try again.',
    
    // Tab Labels
    tabs: {
      overview: 'Overview',
      features: 'Features',
      testCases: 'Test Cases',
      testRuns: 'Test Runs',
      bugs: 'Bugs',
      settings: 'Settings'
    },
    
    // Default Values
    defaults: {
      untitled: 'Untitled Project',
      noDescription: 'No description available',
      unknown: 'Unknown'
    },
    
    // Actions
    customReport: 'Custom Report',
    
    // Messages
    messages: {
      accessDenied: 'Access denied. This is a private project.',
      notFound: 'Project not found.',
      loadingError: 'Failed to load project.',
      userLoadError: 'Failed to load user information.',
      loading: 'Loading project...'
    },
    
    // Project Bugs Page
    bugs: {
      pageTitle: 'Bugs',
      pageDescription: 'Track and manage bugs for this project',
      createBug: 'New Bug',
      viewDeleted: 'Show Deleted',
      hideDeleted: 'Hide Deleted',
      showFilters: 'Show Filters',
      hideFilters: 'Hide Filters',
      exportReport: 'Export Report',
      noBugsTitle: 'No Bugs Found',
      noBugsDescription: 'There are no bugs in this project yet. Create one to get started!',
      loadingError: 'Failed to load bugs',
      exportSuccess: 'Bugs report exported successfully!',
      exportError: 'Failed to export bugs report',
      deletedBadge: 'Deleted',
      
      // Filters
      filters: {
        search: 'Search',
        searchPlaceholder: 'Search by ID, title, or description...',
        status: 'Status',
        priority: 'Priority',
        severity: 'Severity',
        assignedTo: 'Assigned To',
        createdBy: 'Created By',
        team: 'Vertical',
        feature: 'Feature',
        environment: 'Environment',
        browser: 'Browser',
        os: 'Operating System',
        unassigned: 'Unassigned'
      },
      
      // Table
      table: {
        id: 'Id',
        title: 'Title',
        status: 'Status',
        priority: 'Priority',
        severity: 'Severity',
        assignedTo: 'Assigned To',
        environment: 'Environment',
        createdBy: 'Created By',
        createdAt: 'Created',
        actions: 'Actions',
        showing: 'Showing {start} to {end} of {total} bugs',
        showingFiltered: 'Showing {start} to {end} of {total} bugs (filtered from {totalCount} total)'
      },
      
      noDeletedBugsTitle: 'No Deleted Bugs Found',
      noDeletedBugsDescription: 'There are no deleted bugs in this project.',
      deleteBugConfirm: 'This will soft delete the bug. Deleted bugs can be restored from the deleted bugs view. \n Are you sure you want to delete this bug?',
      restoreBugConfirm: 'This will restore the bug. Are you sure you want to restore this bug?',
      bulkDeleteTitle: 'Delete Bugs',
      bulkDeleteTitleSingular: 'Delete Bug',
      bulkDeleteConfirm: 'This will soft delete {count} bugs. Deleted bugs can be restored from the deleted bugs view.',
      bulkDeleteConfirmSingular: 'This will soft delete this bug. It can be restored from the deleted bugs view.',
      bulkDeleteSuccess: 'Bugs deleted successfully!',
      bulkDeletePartial: 'Some bugs could not be deleted ({failed} of {total} failed)',
      bulkDeleteError: 'Failed to delete bugs',
      bulkRestoreTitle: 'Restore Bugs',
      bulkRestoreTitleSingular: 'Restore Bug',
      bulkRestoreConfirm: 'This will restore {count} bugs and make them active again.',
      bulkRestoreConfirmSingular: 'This will restore this bug and make it active again.',
      bulkRestoreSuccess: 'Bugs restored successfully!',
      bulkRestorePartial: 'Some bugs could not be restored ({failed} of {total} failed)',
      bulkRestoreError: 'Failed to restore bugs',
      bulkCloneTitle: 'Clone Bugs',
      clone: 'Clone',
      cloneConfirm: '{count} bugs will be duplicated in this project, each with "(Copy)" added to the title and status reset to "Open".',
      bulkCloneSuccess: 'Bugs cloned successfully!',
      bulkClonePartial: 'Successfully cloned {successful} bug(s), but {failed} of {total} failed',
      bulkCloneError: 'Failed to clone bugs',
      singleCloneTitle: 'Clone Bug',
      singleCloneConfirm: 'A copy of this bug will be created in the same project with "(Copy)" added to the title and status reset to "Open".',
      cloneError: 'Failed to clone bug',
      singleCloneSuccess: 'Bug cloned successfully!',
      bulkEdit: 'Bulk Edit',
      bulkEditDescription: 'Changes will be applied to all selected bugs',
      bulkEditUpdating: 'Updating...',
      noChange: 'No Change',
      selectStatus: 'Select Status',
      selectPriority: 'Select Priority',
      selectSeverity: 'Select Severity',
      selectAssignee: 'Select Assignee',
      selectEnvironment: 'Select Environment',
      noChangesMade: 'No changes were made. Please select at least one field to update.',
      bulkUpdateSuccess: 'Bugs updated successfully!',
      bulkUpdatePartial: 'Some bugs could not be updated ({failed} of {total} failed)',
      bulkUpdateError: 'Failed to update bugs',
      
      // Actions
      actions: {
        view: 'View Details',
        edit: 'Edit Bug',
        delete: 'Delete Bug',
        restore: 'Restore Bug'
      },
      
      // Delete/Restore confirmations
      deleteConfirm: 'Are you sure you want to delete this bug?',
      restoreConfirm: 'Are you sure you want to restore this bug?'
    },
    
    // Project Features Page
    features: {
      pageTitle: 'Features',
      pageDescription: 'Manage features and requirements for this project',
      createFeature: 'New Feature',
      viewDeleted: 'Show Deleted',
      hideDeleted: 'Hide Deleted',
      showFilters: 'Show Filters',
      hideFilters: 'Hide Filters',
      exportReport: 'Export Report',
      noFeaturesTitle: 'No Features Found',
      noFeaturesDescription: 'There are no features in this project yet. Create one to get started!',
      noDeletedFeaturesTitle: 'No Deleted Features Found',
      noDeletedFeaturesDescription: 'There are no deleted features in this project.',
      noResultsTitle: 'No Results Found',
      noResultsDescription: 'No features match your filters. Try adjusting your search criteria.',
      loadingError: 'Failed to load features',
      exportSuccess: 'Features report exported successfully!',
      exportError: 'Failed to export features report',
      deletedBadge: 'Deleted',
      deleteFeature: 'Delete Feature',
      deleteFeatureConfirm: 'This will delete the feature and all its nested child features. Are you sure you want to delete this feature?',
      restoreFeature: 'Restore Feature',
      restoreFeatureConfirm: 'This will restore the feature and all its nested child features. Are you sure you want to restore this feature?',
      restoreOrphanConfirm: 'This feature has a deleted parent. Restoring it will make it a standalone feature (unlinked from its parent). Are you sure you want to restore this feature as a parent?',
      bulkEdit: 'Bulk Edit',
      bulkDeleteTitle: 'Delete Features',
      bulkDeleteTitleSingular: 'Delete Feature',
      bulkDeleteConfirm: 'This will delete {count} features and all their nested child features.',
      bulkDeleteConfirmSingular: 'This will delete this feature and all its nested child features.',
      bulkDeleteSuccess: 'Features deleted successfully!',
      bulkDeletePartial: 'Some features could not be deleted ({failed} of {total} failed)',
      bulkDeleteError: 'Failed to delete features',
      bulkRestoreTitle: 'Restore Features',
      bulkRestoreTitleSingular: 'Restore Feature',
      bulkRestoreConfirm: 'This will restore {count} features and all their nested child features.',
      bulkRestoreConfirmSingular: 'This will restore this feature and all its nested child features.',
      bulkRestoreSuccess: 'Features restored successfully!',
      bulkRestorePartial: 'Some features could not be restored ({failed} of {total} failed)',
      bulkRestoreError: 'Failed to restore features',
      clone: 'Clone',
      cloneTitle: 'Clone Feature',
      bulkCloneTitle: 'Clone Features',
      singleCloneConfirm: 'A copy of this feature will be created in the same project with "(Copy)" added to the title.',
      cloneConfirm: '{count} features will be duplicated in this project, each with "(Copy)" added to the title.',
      cloneSuccess: 'Feature cloned successfully!',
      cloneWithChildrenLabel: 'Clone Options:',
      cloneWithChildren: 'Clone with child features (preserves hierarchy and statuses)',
      cloneWithoutChildren: 'Clone without child features (only the selected feature)',
      cloneParentOptionsLabel: 'Clone as Child Feature:',
      cloneAsStandalone: 'Clone as standalone feature (no parent)',
      cloneUnderSameParent: 'Clone under same parent (stays as child)',
      bulkCloneSuccess: 'Successfully cloned {count} feature(s)!',
      bulkClonePartial: 'Successfully cloned {successful} feature(s), but {failed} of {total} failed',
      cloneError: 'Failed to clone feature',
      bulkCloneError: 'Failed to clone features',
      selectedFeatures: 'selected',
      noChange: 'No Change',
      selectStatus: 'Select Status',
      selectPriority: 'Select Priority',
      selectDeveloper: 'Select Developer',
      selectQAEngineer: 'Select QA Engineer',
      bulkUpdateSuccess: 'Features updated successfully!',
      bulkUpdatePartial: 'Some features could not be updated ({failed} of {total} failed)',
      bulkUpdateError: 'Failed to update features',
      noChangesMade: 'No changes were made to the selected features',
      startDate: 'Start Date',
      endDate: 'End Date',
      dueDate: 'Due Date',
      selectStartDate: 'Select Start Date',
      selectEndDate: 'Select End Date',
      selectDueDate: 'Select Due Date',
      
      // Filters
      filters: {
        searchPlaceholder: 'Search by ID, title, or description...',
        status: 'Status',
        priority: 'Priority',
        assignedTo: 'Assigned To',
        developer: 'Developer',
        qaEngineer: 'QA Engineer',
        team: 'Vertical',
        startDateRange: 'Start Date Range',
        dueDateRange: 'Due Date Range',
        unassigned: 'Unassigned'
      },
      
      // Table
      table: {
        id: 'Id',
        title: 'Title',
        status: 'Status',
        priority: 'Priority',
        developer: 'Developer',
        qaEngineer: 'QA Engineer',
        startDate: 'Start Date',
        dueDate: 'Due Date',
        project: 'Project',
        actions: 'Actions',
        showing: 'Showing {start} to {end} of {total} features',
        showingFiltered: 'Showing {start} to {end} of {total} features (filtered from {totalCount} total)'
      },
      
      // Actions
      actions: {
        view: 'View Details',
        edit: 'Edit Feature',
        delete: 'Delete Feature',
        restore: 'Restore Feature',
        clone: 'Clone Feature'
      }
    },
    
    // Project Test Cases Page
    testCases: {
      pageTitle: 'Test Cases',
      pageDescription: 'Manage test cases for this project',
      createTestCase: 'New Test Case',
      import: 'Import Test Cases',
      viewDeleted: 'Show Deleted',
      hideDeleted: 'Hide Deleted',
      showFilters: 'Show Filters',
      hideFilters: 'Hide Filters',
      exportReport: 'Export',
      exportSelected: 'Export selected ({count})',
      bulkEdit: 'Bulk Edit',
      clone: 'Clone',
      bulkClone: 'Bulk Clone',
      bulkCloneSuccess: 'Successfully cloned {count} test case(s)!',
      bulkCloneError: 'Failed to clone test cases',
      noTestCasesTitle: 'No Test Cases Found',
      noTestCasesDescription: 'There are no test cases in this project yet. Create one to get started!',
      emptyFolderTitle: 'This Folder is Empty',
      emptyFolderDescription: 'This folder doesn\'t contain any test cases yet. Create a test case or view all test cases.',
      emptyFolderWithSubfoldersTitle: 'No Test Cases in This Folder',
      emptyFolderWithSubfoldersDescription: 'This folder doesn\'t have any test cases directly, but its subfolders contain test cases. Expand the subfolders below to view them, or create a test case here.',
      viewAllTestCases: 'View All Test Cases',
      noResultsTitle: 'No Results Found',
      noResultsDescription: 'No test cases match your filters. Try adjusting your search criteria.',
      clearFilters: 'Clear Filters',
      noDeletedTestCasesTitle: 'No Deleted Test Cases',
      allTestCases: 'All Test Cases',
      createFolder: 'Create Folder',
      testSuitesPanelTitle: 'Test Suites',
      findSuitePlaceholder: 'Find test suite...',
      suiteBreadcrumbLabel: 'Test Suites',
      suiteCaseCount: '{count} cases in this suite',
      allSuitesSummary: '{count} test cases in this project',
      suiteLastModified: 'Last modified {date}',
      suiteRowMenuLabel: 'Suite actions',
      suiteMenuEdit: 'Edit',
      suiteMenuDelete: 'Delete',
      suiteMenuAddChild: 'Add subfolder',
      noSuitesMatchSearch: 'No suites match your search.',
      loadingError: 'Failed to load test cases',
      exportSuccess: 'Test cases report exported successfully!',
      exportError: 'Failed to export test cases report',
      deletedBadge: 'Deleted',
      
      tooltips: {
        addFolder: 'Create a test suite to organise test cases'
      },
      
      // Filters
      filters: {
        searchPlaceholder: 'Search by ID, title, or description...',
        type: 'Type',
        status: 'Status',
        priority: 'Priority',
        automationStatus: 'Automation Status',
        assignedTo: 'Assignee',
        folder: 'Folder',
        caseType: 'Case Type',
        estimationRange: 'Estimation Range (minutes)',
        unassigned: 'Unassigned'
      },
      
      // Table
      table: {
        id: 'Test Case Id',
        title: 'Test Case',
        type: 'Type',
        status: 'Status',
        priority: 'Priority',
        automationStatus: 'Automated',
        assignedTo: 'Assigned To',
        estimation: 'Estimation',
        folder: 'Folder',
        actions: 'Actions',
        showing: 'Showing {start} to {end} of {total} test cases',
        showingFiltered: 'Showing {start} to {end} of {total} test cases (filtered from {totalCount} total)',
        minutes: '{count} min'
      },
      
      // Actions
      actions: {
        view: 'View Details',
        edit: 'Edit',
        delete: 'Delete Test Case',
        restore: 'Restore Test Case',
        clone: 'Clone'
      },
      
      // Delete & Restore
      deleteTestCase: 'Delete Test Case',
      deleteConfirm: 'Are you sure you want to delete this test case?',
      restoreTestCase: 'Restore Test Case',
      restoreConfirm: 'Are you sure you want to restore this test case?'
    },
    
    // Project Test Runs Page
    testRuns: {
      pageTitle: 'Test Runs',
      pageDescription: 'Manage test run executions for this project',
      createTestRun: 'New Test Run',
      viewDeleted: 'Show Deleted',
      hideDeleted: 'Hide Deleted',
      showFilters: 'Show Filters',
      hideFilters: 'Hide Filters',
      exportReport: 'Export Report',
      noTestRunsTitle: 'No Test Runs Found',
      noTestRunsDescription: 'There are no test runs in this project yet. Create one to get started!',
      noDeletedTestRunsTitle: 'No Deleted Test Runs',
      noDeletedTestRunsDescription: 'There are no deleted test runs to display.',
      loadingError: 'Failed to load test runs',
      exportSuccess: 'Test runs report exported successfully!',
      exportError: 'Failed to export test runs report',
      deletedBadge: 'Deleted',
      
      // Filters
      filters: {
        searchPlaceholder: 'Search by title or description...',
        status: 'Status',
        environment: 'Environment',
        createdBy: 'Created By',
        estimationRange: 'Estimation Range (minutes)',
        unassigned: 'Unassigned'
      },
      
      // Table
      table: {
        title: 'Title',
        id: 'Id',
        status: 'Status',
        environment: 'Environment',
        estimatedTime: 'Estimated Time',
        createdBy: 'Created By',
        createdAt: 'Created On',
        actions: 'Actions',
        showing: 'Showing {start} to {end} of {total} test runs',
        showingFiltered: 'Showing {start} to {end} of {total} test runs (filtered from {totalCount} total)',
        minutes: '{count} min'
      },
      
      // Actions
      actions: {
        view: 'View Details',
        edit: 'Edit Test Run',
        delete: 'Delete Test Run',
        restore: 'Restore Test Run',
        clone: 'Clone Test Run',
        pause: 'Pause Test Run',
        resume: 'Resume Test Run',
        cancel: 'Cancel Test Run',
        reopen: 'Reopen Test Run',
      },
      
      // Delete/Restore confirmations
      deleteConfirm: 'Are you sure you want to delete this test run?',
      restoreConfirm: 'Are you sure you want to restore this test run?',
      
      noResultsTitle: 'No Results Found',
      noResultsDescription: 'No test runs match your filters. Try adjusting your search criteria.',
      clearFilters: 'Clear Filters'
    },
    
    // Project Folders
    folders: {
      pageTitle: 'Test Suites',
      pageDescription: 'Manage folders for organizing test cases',
      createFolder: 'Create Folder',
      noDeletedFolders: 'No Deleted Folders',
      allFoldersActive: 'All folders are active.',
      
      // Table
      table: {
        name: 'Folder Name',
        description: 'Description',
        parentFolder: 'Parent Folder',
        deletedBy: 'Deleted By',
        deletedAt: 'Deleted At',
        actions: 'Actions'
      }
    },
    
    // Project Settings Page
    settings: {
      pageTitle: 'Settings',
      pageDescription: 'Manage project settings and configuration',
      saveChanges: 'Save Changes',
      saving: 'Saving...',
      cancel: 'Cancel',
      
      // Tabs
      tabs: {
        general: 'General',
        members: 'Members',
        danger: 'Danger Zone'
      },
      
      // Sections
      general: {
        title: 'General Information',
        projectDetails: 'Project Details',
        editProjectInfo: 'Edit Project Information',
        editProjectInfoDescription: 'To modify project details like name, status, type, and visibility, please manage this project from the Verticals page.',
        manageInTeams: 'Manage in Verticals',
        nameLabel: 'Project Name',
        namePlaceholder: 'Enter project name',
        statusLabel: 'Status',
        typeLabel: 'Type',
        visibilityLabel: 'Visibility',
        descriptionLabel: 'Description',
        descriptionPlaceholder: 'Enter project description',
        descriptionHelper: 'Maximum {max} characters',
        maxChars: 'Maximum {max} characters',
        teamLabel: 'Vertical',
        selectTeam: 'Select vertical',
        selectStatus: 'Select status',
        selectType: 'Select type',
        selectVisibility: 'Select visibility'
      },
      
      members: {
        title: 'Project Members',
        addMember: 'Add Member',
        searchPlaceholder: 'Search members...',
        nameLabel: 'Name',
        emailLabel: 'Email',
        role: 'Role',
        actions: 'Actions',
        removeMember: 'Remove Member',
        noMembers: 'No members added yet',
        noMembersDescription: 'Add members to collaborate on this project'
      },
      
      danger: {
        title: 'Danger Zone',
        archiveTitle: 'Archive Project',
        archiveDescription: 'Archive this project to hide it from active lists',
        archiveButton: 'Archive Project',
        deleteTitle: 'Delete Project',
        deleteDescription: 'Permanently delete this project and all associated data',
        deleteButton: 'Delete Project',
        deleteWarning: 'This action cannot be undone',
        deleteConfirmMessage: 'Are you sure you want to delete this project? All data will be permanently removed.'
      },
      
      // Messages
      updateSuccess: 'Project settings updated successfully!',
      updateError: 'Failed to update project settings',
      memberAddedSuccess: 'Member added successfully!',
      memberRemovedSuccess: 'Member removed successfully!',
      memberError: 'Failed to update project member'
    },
    
    // Common project labels
    projectDetails: 'Project Details',
    createdDate: 'Created Date',
    createdBy: 'Created By',
    
    status: {
      active: 'Active',
      paused: 'Paused',
      completed: 'Completed',
      archived: 'Archived',
      inactive: 'Inactive',
      draft: 'Draft'
    },
    
    type: {
      web: 'Web Application',
      mobile: 'Mobile Application',
      api: 'Api/Backend',
      desktop: 'Desktop Application',
      other: 'Other'
    }
  },

  // Edit Member Modal
  member: {
    editTitle: 'Edit Vertical Member',
    unknownUser: 'Unknown User',
    noEmail: 'No email',
    roleLabel: 'Role',
    roleRequired: 'Role *',
    selectRole: 'Select a role',
    roleHelp: 'Choose the appropriate role for this vertical member',
    permissionsTitle: '{role} Permissions',
    saving: 'Saving...',
    saveButton: 'Save Changes',
    updatedSuccess: '{name} role updated successfully',
    updatedFailed: 'Failed to update vertical member role: {error}'
  },

  // Visibility Common
  visibility: {
    public: 'Public',
    private: 'Private',
    publicDescription: 'Visible to all vertical members',
    privateDescription: 'Only visible to Super Admins and members'
  },

  // Validation Messages
  validation: {
    fieldRequired: 'This field is required',
    minLength: 'Must be at least {min} characters',
    maxLength: 'Must be less than {max} characters',
    duplicateName: 'A {type} with this name already exists',
    invalidEmail: 'Please enter a valid email address',
    passwordTooShort: 'Password must be at least {min} characters',
    minMembersRequired: 'At least {min} member is required',
    teamNameTooShort: 'Vertical name must be at least 2 characters',
    teamNameTooLong: 'Vertical name must be less than 50 characters',
    projectTitleTooShort: 'Project title must be at least 3 characters',
    projectTitleTooLong: 'Project title must be less than 100 characters',
    duplicateTeam: 'A vertical with this name already exists',
    duplicateProject: 'A project with this name already exists',
    pleaseSelectTeam: 'Please select a vertical'
  },

  // Role Permissions
  roles: {
    permissions: {
      owner: {
        manageProjects: 'Manage projects within verticals',
        manageMembers: 'Manage vertical members',
        manageTestCases: 'Manage test cases and runs',
        manageBugs: 'Triage, assign, and close bugs',
        viewReports: 'View and generate reports',
        configureSettings: 'Configure vertical settings'
      },
      user: {
        executeTests: 'Execute test cases',
        reportBugs: 'Report bugs (including cross-vertical)',
        viewAssigned: 'View assigned test cases',
        addComments: 'Add comments',
        viewReports: 'View project reports'
      }
    }
  },

  // List Component
  list: {
    actions: 'Actions',
    item: 'Item',
    noItems: 'No items available',
    itemSelected: 'Item selected',
    ariaLabel: 'List of items',
    listOf: 'List of {count} items'
  },

  // Form Component
  form: {
    submit: 'Submit',
    cancel: 'Cancel',
    submitting: 'Submitting...',
    save: 'Save',
    saving: 'Saving...',
    reset: 'Reset',
    resetting: 'Resetting...',
    clear: 'Clear',
    clearing: 'Clearing...',
    requiredField: 'This field is required',
    invalidField: 'This field is invalid',
    formError: 'Please correct the errors below',
    formTitle: 'Form',
    formDescription: 'Fill out the form below'
  },

  // ============================================================================
  // AUTH MODULE
  // ============================================================================
  auth: {
    // Login Page
    login: {
      title: 'Sign in to your account',
      subtitle: 'Or',
      createAccountLink: 'create a new account',
      emailPlaceholder: 'Email address',
      passwordPlaceholder: 'Password',
      rememberMe: 'Remember me',
      forgotPassword: 'Forgot your password?',
      signingIn: 'Signing in...',
      signIn: 'Sign in',
      ssoIntro: 'Or continue with',
      signInWithGoogle: 'Sign in with Google',
      googleNotConfigured: '⚠️ Google OAuth not configured. Add VITE_GOOGLE_CLIENT_ID to .env',
      googleDomainRestriction: 'Google sign-in is restricted to @gohighlevel.com email addresses',
      demoCredentials: 'Demo Credentials',
      demoAdmin: 'Admin:',
      demoQAManager: 'Qa Manager:',
      demoTester: 'Tester:'
    },
    
    // Register Page
    register: {
      title: 'Create your account',
      subtitle: 'Or',
      signInLink: 'sign in to your existing account',
      fullNameLabel: 'Full Name',
      fullNamePlaceholder: 'Enter your full name',
      emailLabel: 'Email Address',
      emailPlaceholder: 'your.name@gohighlevel.com',
      emailHint: 'Only @gohighlevel.com email addresses are allowed',
      passwordLabel: 'Password',
      passwordPlaceholder: 'Create a password',
      confirmPasswordLabel: 'Confirm Password',
      confirmPasswordPlaceholder: 'Confirm your password',
      roleLabel: 'Role',
      termsPrefix: 'I agree to the',
      termsLink: 'Terms and Conditions',
      termsAnd: 'and',
      privacyLink: 'Privacy Policy',
      creatingAccount: 'Creating account...',
      createAccount: 'Create account'
    },
    
    // Forgot Password Page
    forgotPassword: {
      title: 'Reset your password',
      subtitle: 'Enter your email address and we\'ll send you a reset link',
      emailLabel: 'Email address',
      emailPlaceholder: 'Enter your email address',
      sending: 'Sending...',
      sendResetLink: 'Send reset link',
      backToLogin: 'Back to login',
      successTitle: 'Reset link sent!',
      successMessage: 'If an account exists with <strong>{email}</strong>, a password reset link has been sent.',
      devModeTitle: 'Development Mode:',
      devModeMessage: 'Click the link below to reset your password:',
      resetPasswordButton: 'Reset Password',
      emailNotReceived: 'Didn\'t receive the email? Check your spam folder.',
      tryAgain: 'Try again'
    },
    
    // Reset Password Page
    resetPassword: {
      title: 'Set new password',
      subtitle: 'Enter your new password below',
      newPasswordLabel: 'New Password',
      newPasswordPlaceholder: 'Enter new password',
      passwordHint: 'Must be at least 6 characters',
      confirmPasswordLabel: 'Confirm Password',
      confirmPasswordPlaceholder: 'Confirm new password',
      resetting: 'Resetting...',
      resetPassword: 'Reset password',
      backToLogin: 'Back to login',
      successTitle: 'Password reset successful!',
      successMessage: 'Your password has been reset successfully. You can now login with your new password.',
      goToLogin: 'Go to login'
    }
  },

  // ============================================================================
  // TEAMS MODULE
  // ============================================================================
  teams: {
    // Page Header
    title: 'Vertical Management',
    description: 'Manage verticals and their projects across the system',
    helperText: 'Create, organize, and manage verticals with projects',

    // Actions
    searchTeams: 'Search verticals...',
    searchPlaceholder: 'Search verticals...',
    searchProjectsPlaceholder: 'Search projects...',
    showFilters: 'Show Filters',
    hideFilters: 'Hide Filters',
    viewDeleted: 'Show Deleted',
    hideDeleted: 'Hide Deleted',
    refreshTeams: 'Refresh',
    createTeam: 'New Vertical',
    createProject: 'New Project',
    manageTeam: 'Manage Vertical',
    editTeam: 'Edit Vertical',
    editProject: 'Edit Project',
    deleteTeam: 'Delete Vertical',
    deleteProject: 'Delete Project',
    restoreTeam: 'Restore Vertical',
    restoreProject: 'Restore Project',
    backToTeams: 'Back to Verticals',

    // Table Columns
    columns: {
      teamName: 'Vertical Name',
      team: 'Vertical Name',
      description: 'Description',
      projects: 'Projects',
      created: 'Created',
      actions: 'Actions'
    },

    // Project Columns (for Vertical Details page)
    projectColumns: {
      project: 'Project Name',
      description: 'Description',
      status: 'Status',
      created: 'Created',
      actions: 'Actions'
    },

    // Loading and Empty States
    loadingTeams: 'Loading verticals...',
    noTeamsFound: 'No verticals found',
    noTeamsDescription: 'Create your first vertical to get started',
    noDeletedTeamsFound: 'No deleted verticals found',
    noDeletedTeamsDescription: 'All verticals are active',
    noDescription: 'No description',
    deletedBadge: 'Deleted',

    // Vertical Details
    noMembersOnTeam: 'No members',
    noMembersDescription: 'This vertical has no members yet',
    noEmail: 'No email',
    projectsTab: 'Projects',
    membersTab: 'Members',
    searchMembersPlaceholder: 'Search members...',
    teamNotFound: 'Vertical not found',
    teamNotFoundDescription: 'The vertical you\'re looking for doesn\'t exist or has been deleted',
    loadingTeamDetails: 'Loading vertical details...',
    noProjectsFound: 'No projects found',
    noProjectsDescription: 'Create your first project to get started',
    noDeletedProjectsFound: 'No deleted projects found',
    allProjectsActive: 'All projects are active',
    
    // Counts and Stats
    membersCount: '{count} member{plural}',
    projectsCount: '{count} project{plural}',
    moreProjects: '+{count} more',
    
    // Visibility
    visibilityPublic: 'Public',
    visibilityPrivate: 'Private',

    // Success Messages
    teamCreatedSuccess: 'Vertical created successfully!',
    teamUpdatedSuccess: 'Vertical updated successfully!',
    teamDeletedSuccess: 'Vertical deleted successfully',
    teamRestoredSuccess: 'Vertical and all its resources restored successfully!',
    projectCreatedSuccess: 'Project created successfully!',
    projectUpdatedSuccess: 'Project updated successfully!',
    projectDeletedSuccess: 'Project deleted successfully',
    projectRestoredSuccess: 'Project and all its resources restored successfully!',

    // Error Messages
    failedToLoadTeams: 'Failed to load verticals',
    failedToLoadTeamDetails: 'Failed to load vertical details',
    failedToCreateTeam: 'Failed to create vertical',
    failedToUpdateTeam: 'Failed to update vertical',
    failedToDeleteTeam: 'Failed to delete vertical',
    failedToRestoreTeam: 'Failed to restore vertical',
    failedToLoadProjects: 'Failed to load projects',
    failedToCreateProject: 'Failed to create project',
    failedToUpdateProject: 'Failed to update project',
    failedToDeleteProject: 'Failed to delete project',
    failedToRestoreProject: 'Failed to restore project',
    accessDenied: 'Access denied. This is a private vertical.',

    // Delete Confirmation
    deleteConfirmTitle: 'Delete Vertical',
    deleteConfirmDescription: 'Are you sure you want to delete "{name}"?',
    deleteWarning: 'This action will delete the vertical.',
    restoreConfirmTitle: 'Restore Vertical',
    restoreConfirmDescription: 'Are you sure you want to restore "{name}"?',
    restoreInfo: 'All deleted resources associated with this vertical will be restored automatically.',

    // Deletion Options
    deletion: {
      dataAffectedTitle: 'Data to be affected:',
      whatToDo: 'What would you like to do with this data?',
      selectDestinationTeam: 'Select destination vertical:',
      chooseTeam: 'Select a vertical to migrate to',
      pleaseSelectTeam: 'Please select a destination vertical',
      finalConfirmation: 'Final Confirmation Required',
      confirmMigration: 'Are you sure you want to delete {teamName} and move all its data to {destinationName}?',
      confirmDeletion: 'Are you sure you want to delete {teamName} and mark all its associated data as deleted? You can restore them later from the deleted sections.',
      migrateLabel: 'Move all data to another vertical',
      migrateDescription: 'Transfer projects, features, test cases, test runs, bugs, and folders to a different vertical',
      deleteLabel: 'Delete all data',
      deleteDescription: 'This will delete all projects, features, test cases, test runs, bugs, and folders',
      processing: 'Processing...',
      deleteButton: 'Delete Vertical',
      migrationSuccess: 'Vertical data successfully migrated to {destinationName}',
      deletionSuccess: 'Vertical deleted successfully!\n\nDeleted: {summary}\n\nYou can restore from the deleted verticals section.',
      restoredSummary: 'Projects: {projects}, Features: {features}, Test Cases: {testCases}, Test Runs: {testRuns}, Bugs: {bugs}, Folders: {folders}',
      resourcesWillBeRestored: 'Resources that will be restored:',
      selectResourcesToRestore: 'Select resources to restore:',
      restoreWarning: 'This will restore the vertical and selected deleted resources. Active resources will remain unchanged.',
      confirmTypeNameLabel: 'To confirm, type {name} below',
      confirmTypeNamePlaceholder: 'Type the name to confirm',
      confirmNameMismatch: 'Name does not match'
    }
  },

  // ============================================================================
  // TEAM MODAL
  // ============================================================================
  teamModal: {
    // Modal Titles
    createTitle: 'Create New Vertical',
    editTitle: 'Edit Vertical',

    // Form Labels
    nameLabel: 'Vertical Name',
    nameRequired: 'Vertical Name *',
    namePlaceholder: 'Enter vertical name',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'Enter vertical description (optional)',
    membersLabel: 'Vertical Members',
    membersRequired: 'Vertical Members *',
    membersPlaceholder: 'Select vertical members',
    membersSearchPlaceholder: 'Search members...',
    membersHelp: 'Select at least one vertical member',
    membersCount: '{count} member{plural} selected',
    visibilityLabel: 'Vertical Visibility',
    visibilityHelp: 'Private verticals are only visible to Super Admins and vertical members',

    // Visibility Options
    visibilityPublic: 'Public',
    visibilityPublicDescription: 'Visible to all users',
    visibilityPrivate: 'Private',
    visibilityPrivateDescription: 'Only visible to Super Admins and vertical members',

    // States
    creating: 'Creating...',
    updating: 'Updating...',
    createButton: 'Create Vertical Only',
    createAndOpenButton: 'Create and Open Vertical',
    updateButton: 'Update Vertical',

    // Empty States
    noUsersFound: 'No users found',
    noUsersAvailable: 'No users available',
    loadingUsers: 'Loading users...',
    unknownUser: 'Unknown User',

    // Validation
    nameError: 'Vertical name is required',
    nameTooShort: 'Vertical name must be at least 2 characters',
    nameTooLong: 'Vertical name must be less than 50 characters',
    duplicateName: 'A vertical with this name already exists',
    membersError: 'At least one vertical member is required',

    // Errors
    userNotAuthenticated: 'User not authenticated',
    createdFailed: 'Failed to create vertical',
    updatedFailed: 'Failed to update vertical'
  },

  // ============================================================================
  // PROJECT MODAL
  // ============================================================================
  projectModal: {
    // Modal Titles
    createTitle: 'Create New Project',
    editTitle: 'Edit Project',
    createSubtitle: 'Add a project, vertical, and members.',
    editSubtitle: 'Update details, members, and visibility.',

    // Form Labels
    titleLabel: 'Project Name',
    titleRequired: 'Project Name *',
    titlePlaceholder: 'Enter project name',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'Enter project description (optional)',
    teamLabel: 'Vertical',
    teamRequired: 'Vertical *',
    selectTeam: 'Select a vertical',
    createNewTeam: 'Create New Vertical',
    newTeamNameLabel: 'New Vertical Name',
    newTeamNameRequired: 'New Vertical Name *',
    newTeamNamePlaceholder: 'Enter new vertical name',
    statusLabel: 'Status',
    typeLabel: 'Project Type',
    membersLabel: 'Project Members',
    membersPlaceholder: 'Select project members',
    membersSearchPlaceholder: 'Search members...',
    membersCount: '{count} member{plural} selected',
    visibilityLabel: 'Project Visibility',
    visibilityHelp: 'Private projects are only visible to Super Admins and project members',
    
    // Visibility Options
    visibilityPublic: 'Public',
    visibilityPublicDescription: 'Visible to all vertical members',
    visibilityPrivate: 'Private',
    visibilityPrivateDescription: 'Only visible to Super Admins and project members',

    // States
    creating: 'Creating...',
    updating: 'Updating...',
    createButton: 'Create Project Only',
    createAndOpenButton: 'Create and Open Project',
    updateButton: 'Update Project',
    newVerticalSelected: 'New Vertical',

    // Empty States
    noUsersFound: 'No users found',
    noUsersAvailable: 'No users available',
    loadingUsers: 'Loading users...',
    loadingTeams: 'Loading verticals...',

    // Validation
    titleError: 'Project name is required',
    titleTooShort: 'Project name must be at least 3 characters',
    titleTooLong: 'Project name must be less than 100 characters',
    duplicateName: 'A project with this name already exists',
    teamError: 'Please select a vertical',
    newTeamNameError: 'New vertical name is required',
    
    // Errors
    userNotAuthenticated: 'User not authenticated',
    createdFailed: 'Failed to create project',
    updatedFailed: 'Failed to update project',
    duplicateError: 'A project with this name already exists',
    networkError: 'Network error. Please check your connection.',
    validationError: 'Please fix the validation errors'
  },

  // ============================================================================
  // CUSTOM REPORT MODAL
  // ============================================================================
  customReport: {
    // Modal Header
    title: 'Create Custom Report',
    description: 'Build a custom report by selecting fields and defining filters.',
    
    // Basic Information
    reportName: 'Report Name',
    reportNamePlaceholder: 'Enter report name',
    reportType: 'Report Type',
    reportTypeSelect: 'Select Type',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'Describe the purpose of this report',
    
    // Report Types
    types: {
      features: 'Features',
      testCases: 'Test Cases',
      testRuns: 'Test Runs',
      bugs: 'Bugs',
      execution: 'Test Execution',
      coverage: 'Test Coverage',
      automation: 'Automation Status'
    },
    
    // Fields Section
    selectFieldsTitle: 'Select Fields',
    availableFields: 'Available Fields',
    selectedFields: 'Selected Fields',
    noFieldsSelected: 'No fields selected',
    noFieldsSelectedDescription: 'Select fields from the available list to include in your report',
    
    // Field Labels
    fields: {
      title: 'Title',
      description: 'Description',
      name: 'Name',
      status: 'Status',
      priority: 'Priority',
      severity: 'Severity',
      type: 'Type',
      startDate: 'Start Date',
      dueDate: 'Due Date',
      devAssignee: 'Dev Assignee',
      qaAssignee: 'Qa Assignee',
      assignedTo: 'Assigned To',
      testRuns: 'Test Runs',
      tags: 'Tags',
      createdBy: 'Created By',
      createdAt: 'Created At',
      updatedAt: 'Updated At',
      automationStatus: 'Automation Status',
      folder: 'Feature/Folder',
      estimatedTime: 'Estimated Time',
      environment: 'Environment',
      features: 'Features',
      completedAt: 'Completed At',
      totalCases: 'Total Cases',
      passedCases: 'Passed Cases',
      failedCases: 'Failed Cases',
      blockedCases: 'Blocked Cases',
      bugId: 'Bug Id',
      stepsToReproduce: 'Steps to Reproduce',
      expectedResult: 'Expected Result',
      actualResult: 'Actual Result',
      linkedTestCases: 'Linked Test Cases',
      testRunName: 'Test Run Name',
      testCaseName: 'Test Case Name',
      executedBy: 'Executed By',
      executedAt: 'Executed At',
      duration: 'Duration',
      notes: 'Notes',
      bugsFound: 'Bugs Found',
      feature: 'Feature',
      totalTestCases: 'Total Test Cases',
      automatedCases: 'Automated Cases',
      manualCases: 'Manual Cases',
      coveragePercentage: 'Coverage %',
      automationPercentage: 'Automation %',
      framework: 'Framework',
      lastExecuted: 'Last Executed',
      executionCount: 'Execution Count',
      successRate: 'Success Rate %'
    },
    
    // Filters Section
    filtersTitle: 'Filters',
    addFilter: 'Add Filter',
    filterNumber: 'Filter {number}',
    fieldLabel: 'Field',
    selectField: 'Select Field',
    operatorLabel: 'Operator',
    selectOperator: 'Select Operator',
    valueLabel: 'Value',
    selectValue: 'Select Value',
    enterValue: 'Enter value',
    
    // Operators
    operators: {
      equals: 'Equals',
      notEquals: 'Not Equals',
      contains: 'Contains',
      notContains: 'Not Contains',
      greaterThan: 'Greater Than',
      lessThan: 'Less Than',
      between: 'Between',
      in: 'In',
      notIn: 'Not In'
    },
    
    // Export Options
    exportOptionsTitle: 'Export Options',
    formatLabel: 'Format',
    scheduleLabel: 'Schedule',
    
    // Export Formats
    formats: {
      pdf: 'Pdf',
      excel: 'Excel',
      csv: 'Csv',
      json: 'Json'
    },
    
    // Schedule Options
    schedules: {
      once: 'Generate Once',
      daily: 'Daily',
      weekly: 'Weekly',
      monthly: 'Monthly'
    },
    
    // Actions
    createReport: 'Create Report',
    creating: 'Creating...',
    cancel: 'Cancel',
    remove: 'Remove',
    
    // Messages
    success: 'Report "{name}" created successfully!',
    enterReportName: 'Please enter a report name',
    selectReportType: 'Please select a report type',
    selectAtLeastOneField: 'Please select at least one field',
    failedToCreate: 'Failed to create report. Please try again.',
    
    // ARIA Labels
    closeModal: 'Close modal',
    reportIcon: 'Report icon',
    removeField: 'Remove field',
    removeFilter: 'Remove filter'
  }
}

/**
 * Helper function to get a nested string from the UI_STRINGS object
 * @param {string} key - Dot-notation key (e.g., 'searchBar.placeholder')
 * @param {Object} params - Parameters to replace in the string (e.g., { count: 5 })
 * @returns {string} The translated string with parameters replaced
 */
export function t(key, params = {}) {
  const keys = key.split('.')
  let value = UI_STRINGS
  
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k]
    } else {
      console.warn(`Translation key not found: ${key}`)
      return key
    }
  }
  
  if (typeof value !== 'string') {
    console.warn(`Translation value is not a string: ${key}`)
    return key
  }
  
  // Replace parameters in the format {paramName}
  return (value as string).replace(/\{(\w+)\}/g, (match, paramName) => {
    return params[paramName] !== undefined ? params[paramName] : match
  })
}

/**
 * Helper function to get pluralized string
 * @param {string} singularKey - Key for singular form
 * @param {string} pluralKey - Key for plural form
 * @param {number} count - Count to determine singular/plural
 * @param {Object} params - Additional parameters
 * @returns {string} The translated string
 */
export function tPlural(singularKey, pluralKey, count, params = {}) {
  const key = count === 1 ? singularKey : pluralKey
  return t(key, { count, ...params })
}

export default UI_STRINGS
